import { useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import { FeatureIcon } from '@rossum/ui';
import { IconCalendarEvent } from '@rossum/ui/icons/tabler';
import {
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { unleashClient } from '../../../unleash/Unleash';
import {
  calendlyLinks,
  calendlyLinkVariants,
} from '../../surveys/DiscoveryCallSurvey/calendlyLinks';
import {
  discoveryCallsList,
  FeatureFlagSlug,
} from '../../surveys/DiscoveryCallSurvey/discoveryCallsList';
import {
  discoveryCallsPrefix,
  getDiscoveryCallsFlag,
} from '../../surveys/DiscoveryCallSurvey/getDiscoveryCallsFlag';

type DiscoveryCallTileProps = {
  triggerUrl: string;
  name: string;
  featureFlagSlug: FeatureFlagSlug;
  discoveryCallFlag: ReturnType<typeof getDiscoveryCallsFlag>;
};

const DiscoveryCallTile = ({
  triggerUrl,
  name: flagName,
  featureFlagSlug,
  discoveryCallFlag,
}: DiscoveryCallTileProps) => {
  const { name, enabled, calendlyLink } = discoveryCallFlag ?? {
    enabled: false,
    calendlyLink: null,
    name: `${discoveryCallsPrefix}-${featureFlagSlug}__(${calendlyLinkVariants.join('|')})`,
  };

  const calendlyLinkKey =
    R.entries(calendlyLinks).find(
      ([_, value]) => value === calendlyLink
    )?.[0] ?? '--';

  const calendlyLinkToDisplay =
    calendlyLinkKey.length > 3
      ? R.capitalize(calendlyLinkKey)
      : R.toUpperCase(calendlyLinkKey);

  return (
    <>
      <Divider />
      <ListItem
        sx={{
          gap: 3,
          px: 4,
          py: 2,
          alignItems: 'center',
          [`& .MuiListItemSecondaryAction-root`]: {
            alignSelf: 'flex-start',
            position: 'relative',
            transform: 'none',
            top: 0,
          },
        }}
        secondaryAction={
          <Chip
            label={
              calendlyLink ? 'Enabled' : enabled ? 'Missing link' : 'Disabled'
            }
            color={calendlyLink ? 'success' : enabled ? 'warning' : 'default'}
          />
        }
      >
        <ListItemIcon sx={{ minWidth: 40, alignSelf: 'flex-start', pt: 1 }}>
          <FeatureIcon content={IconCalendarEvent} color="secondary" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant="body1"
              fontWeight="bold"
              noWrap
              component="span"
            >
              {flagName}
            </Typography>
          }
          secondary={
            <Stack component="span">
              <Stack
                spacing={1}
                direction="row"
                alignItems="center"
                component="span"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                >
                  {name}
                </Typography>
                <CopyToClipboardButton
                  buttonProps={{ size: 'small', color: 'inherit' }}
                  content={name}
                />
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                component="span"
                divider={
                  <Typography variant="body2" component="span">
                    •
                  </Typography>
                }
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                >
                  {`Trigger URL: ${triggerUrl}`}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                >
                  <span>{`Calendly link: `}</span>{' '}
                  <Tooltip title={calendlyLink}>
                    <span>{calendlyLinkToDisplay}</span>
                  </Tooltip>
                </Typography>
              </Stack>
            </Stack>
          }
        />
      </ListItem>
    </>
  );
};

export const DiscoveryCallSurveysPage = () => {
  const [refreshTimestamp, setRefreshTimestamp] = useState(0);
  const intl = useIntl();

  const sortedDiscoveryCalls = useMemo(() => {
    const allFlags = unleashClient.getAllToggles();

    return R.pipe(
      discoveryCallsList,
      R.map(dc => ({
        ...dc,
        discoveryCallFlag: getDiscoveryCallsFlag(allFlags, dc.featureFlagSlug),
      })),
      R.sortBy([dc => dc.discoveryCallFlag?.enabled !== true, 'asc'])
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTimestamp]);

  return (
    <Paper>
      <Stack key={refreshTimestamp} spacing={4} sx={{ width: '100%' }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ p: 4, pb: 0 }}
        >
          <Typography variant="h5">
            {intl.formatMessage({
              id: 'features.personalSettings.discoveryCallSurveys',
            })}
          </Typography>
          <Button
            onClick={() =>
              unleashClient
                .updateContext({})
                .then(() => setRefreshTimestamp(Date.now()))
            }
            variant="outlined"
          >
            Refresh flags
          </Button>
        </Stack>
        <List disablePadding dense sx={{ width: '100%' }}>
          {sortedDiscoveryCalls.map(dc => (
            <DiscoveryCallTile
              key={dc.featureFlagSlug}
              triggerUrl={dc.triggerUrl}
              name={dc.name}
              featureFlagSlug={dc.featureFlagSlug}
              discoveryCallFlag={dc.discoveryCallFlag}
            />
          ))}
        </List>
      </Stack>
    </Paper>
  );
};
